import React from "react";
import {
  Table,
  Segment,
  Button,
  Input,
  Message,
  Label,
  Header,
} from "semantic-ui-react";
import { Form, Formik, getIn, FieldArray, Field } from "formik";
import * as Yup from "yup";

const InventoryStep = ({
  supplyUnits,
  setSupplyUnits,
  onStepComplete,
  onPrevious,
  inventory,
}) => {
  // Filter units that were marked for new inventory assignment
  const unitsToProcess = supplyUnits.filter(
    (unit) => unit.assign_new_inventory
  );

  // Group units by their new_inventory_id
  const groupedUnits = unitsToProcess.reduce((groups, unit) => {
    const groupId = unit.new_inventory_id;
    if (!groups[groupId]) {
      groups[groupId] = [];
    }
    groups[groupId].push(unit);
    return groups;
  }, {});

  // Prepare initial values for the form
  const initialValues = {
    inventoryGroups: Object.entries(groupedUnits).map(([groupId, units]) => ({
      groupId: parseInt(groupId),
      title: inventory.inventory_title || "",
      shelf: units[0].new_inventory_shelf || "",
      units: units.map((unit) => ({
        upc: unit.upc,
        title: unit.supply_title,
        inv: unit.inv || 0, // Include inv value, default to 0 if not present
        originalIndex: supplyUnits.findIndex((u) => u.upc === unit.upc),
      })),
    })),
  };

  // Create validation schema
  const validationSchema = Yup.object({
    inventoryGroups: Yup.array().of(
      Yup.object({
        groupId: Yup.number().required(),
        title: Yup.string().required("Inventory title is required"),
        shelf: Yup.string().required("Shelf location is required"),
        units: Yup.array().of(
          Yup.object({
            upc: Yup.string().required(),
            title: Yup.string().required(),
            inv: Yup.number(),
            originalIndex: Yup.number().required(),
          })
        ),
      })
    ),
  });

  // Calculate total inventory for a group
  const calculateTotalInventory = (units) => {
    return units.reduce((sum, unit) => sum + (unit.inv || 0), 0);
  };

  // Handle form submission
  const handleSubmit = (values, { setSubmitting }) => {
    // Update the supply units with the new inventory information
    const updatedUnits = [...supplyUnits];

    values.inventoryGroups.forEach((group) => {
      group.units.forEach((unit) => {
        updatedUnits[unit.originalIndex] = {
          ...updatedUnits[unit.originalIndex],
          new_inventory_title: group.title,
          new_inventory_shelf: group.shelf,
        };
      });
    });

    setSupplyUnits(updatedUnits);
    onStepComplete(values);
    setSubmitting(false);
  };

  // Custom Input component for Formik
  const FormikInput = ({ field, form, ...props }) => {
    return (
      <Input
        {...field}
        {...props}
        value={field.value || ""}
        onChange={(e, { value }) => form.setFieldValue(field.name, value)}
      />
    );
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize={true}
    >
      {({ isSubmitting, errors, touched, values }) => (
        <Form>
          <Segment>
            <Header as="h4">Define Inventory Details</Header>
            <p>
              Provide a title and shelf location for each inventory group you
              created.
            </p>

            {Object.keys(errors).length > 0 && touched.inventoryGroups && (
              <Message negative>
                <Message.Header>
                  Please complete all inventory details
                </Message.Header>
                <p>
                  Each inventory group must have a title and shelf location
                  before proceeding.
                </p>
              </Message>
            )}

            <FieldArray name="inventoryGroups">
              {() => (
                <div>
                  {values.inventoryGroups.map((group, groupIndex) => {
                    const titleField = `inventoryGroups[${groupIndex}].title`;
                    const shelfField = `inventoryGroups[${groupIndex}].shelf`;

                    const titleError = getIn(errors, titleField);
                    const titleTouched = getIn(touched, titleField);

                    const shelfError = getIn(errors, shelfField);
                    const shelfTouched = getIn(touched, shelfField);

                    // Calculate total inventory for this group
                    const totalInventory = calculateTotalInventory(group.units);

                    return (
                      <Segment key={group.groupId}>
                        <Header as="h5">
                          Group {Math.abs(group.groupId)} - {group.units.length}{" "}
                          item(s)
                        </Header>

                        <div
                          style={{
                            display: "flex",
                            gap: "20px",
                            marginBottom: "15px",
                          }}
                        >
                          <div style={{ flex: "1" }}>
                            <label htmlFor={titleField}>Inventory Title</label>
                            <Field
                              name={titleField}
                              component={FormikInput}
                              fluid
                              placeholder="Enter inventory title"
                              error={titleTouched && titleError ? true : false}
                            />
                            {titleTouched && titleError ? (
                              <Label pointing prompt>
                                {titleError}
                              </Label>
                            ) : null}
                          </div>

                          <div style={{ flex: "1" }}>
                            <label htmlFor={shelfField}>Shelf Location</label>
                            <Field
                              name={shelfField}
                              component={FormikInput}
                              fluid
                              placeholder="Enter shelf location"
                              error={shelfTouched && shelfError ? true : false}
                            />
                            {shelfTouched && shelfError ? (
                              <Label pointing prompt>
                                {shelfError}
                              </Label>
                            ) : null}
                          </div>
                        </div>

                        {/* Display Total Inventory */}
                        <div style={{ marginBottom: "10px" }}>
                          <strong>Total Inventory: {totalInventory}</strong>
                        </div>

                        <Table compact>
                          <Table.Header>
                            <Table.Row>
                              <Table.HeaderCell>UPC</Table.HeaderCell>
                              <Table.HeaderCell>Title</Table.HeaderCell>
                              <Table.HeaderCell>Inventory</Table.HeaderCell>
                            </Table.Row>
                          </Table.Header>
                          <Table.Body>
                            {group.units.map((unit) => (
                              <Table.Row key={unit.upc}>
                                <Table.Cell>{unit.upc}</Table.Cell>
                                <Table.Cell>{unit.supply_title}</Table.Cell>
                                <Table.Cell>{unit.inv || 0}</Table.Cell>
                              </Table.Row>
                            ))}
                          </Table.Body>
                        </Table>
                      </Segment>
                    );
                  })}
                </div>
              )}
            </FieldArray>

            {/* Action Buttons */}
            <div
              style={{
                marginTop: "20px",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Button type="button" onClick={onPrevious}>
                Back
              </Button>
              <Button type="submit" primary disabled={isSubmitting}>
                Next
              </Button>
            </div>
          </Segment>
        </Form>
      )}
    </Formik>
  );
};

export default InventoryStep;
